import snakecaseKeys from 'snakecase-keys';
import {
  GET_USER_PROFILE_MESSAGE_TYPE,
  setUserProfile,
  UserProfile,
} from '@leagueplatform/user-profile-api';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useGetUserProfile } from './use-get-user-profile.hook';
import { usePreferredFirstName } from './use-preferred-first-name.hook';

export const usePersonalInformation = () => {
  const { data, isLoading, error } = useGetUserProfile();

  const {
    data: firstNameData,
    isLoading: firstNameIsLoading,
    error: firstNameError,
  } = usePreferredFirstName();

  const fullName = `${firstNameData?.preferredFirstName?.toUpperCase()} ${
    data?.userProfile.lastName ?? ''
  }`;
  const userInitials = `${firstNameData?.preferredFirstName?.[0] ?? ''}${
    data?.userProfile?.lastName?.[0] ?? ''
  }`;

  const initialValues = {
    preferredFirstName: data?.userProfile?.preferredFirstName,
    dateOfBirth: data?.userProfile?.dateOfBirth,
    pronouns: data?.userProfile?.pronouns,
    genderIdentity: data?.userProfile?.genderIdentity,
  };

  return {
    data: { ...data, fullName, userInitials, initialValues },
    isLoading: isLoading || firstNameIsLoading,
    error: error ?? firstNameError,
  };
};

/**
 * @name useUpdateUserProfile
 * @desc React hook to update the user profile
 * @returns Function that updates user profile
 */

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserProfileMutation, isLoading } = useMutation({
    mutationFn: (userProfile: Partial<UserProfile>) =>
      setUserProfile(userProfile),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_USER_PROFILE_MESSAGE_TYPE],
      }),
  });

  const updateUserProfile = async (userProfile: any) => {
    const serializedBody = snakecaseKeys(userProfile);
    return updateUserProfileMutation(serializedBody);
  };

  return { updateUserProfile, isLoading };
};
